import { useEffect, useState } from 'react';

import { useAppContext, useFacilityContext } from '@shared-components/context';
import { BffBrandType } from '@shared-components/gqlConstants';
import { getBffAvailabilityByLocation } from '@shared-components/services/getBffAvailabilityByLocation';

export type NextAppointmentSlot = {
	time: string;
	date: string;
};

export function convertTimeTo12HourFormat(time: string): string {
	if (!time) return '';

	const [hourStr, minute] = time.split(':');
	let hour = Number.parseInt(hourStr, 10);
	const ampm = hour >= 12 ? 'PM' : 'AM';
	hour = hour % 12 || 12;

	return `${hour}:${minute} ${ampm}`;
}

function toIsoDate(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

export function useNextAppointment(type: string) {
	const { officeInfo } = useFacilityContext(true);
	const { environment, config, appName, appVersion } = useAppContext();
	const siteUrl = environment === 'prod' ? config.siteURL : config.siteURLStage;
	const url =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
	const [appointment, setAppointment] = useState<NextAppointmentSlot | null>(null);
	const [scheduleText, setScheduleText] = useState('Now');

	useEffect(() => {
		if (!officeInfo) return;

		const fetchFirstAppointment = async () => {
			try {
				const startDate = new Date();
				const endDate = new Date(startDate);
				endDate.setDate(startDate.getDate() + 8);

				const availabilityData = await getBffAvailabilityByLocation(
					BffBrandType.Lovet,
					officeInfo.code.replace(/^LV_/, ''),
					url,
					appName,
					appVersion,
					startDate,
					endDate
				);

				const allSlots = availabilityData?.availabilityByLocation?.availableSlots;
				if (!allSlots) return;

				const firstSlot = allSlots.find((slot) => slot?.type?.toLowerCase() === type);
				if (!firstSlot) {
					setAppointment(null);
					return;
				}

				setAppointment(firstSlot);
				if (firstSlot.date === toIsoDate(startDate)) {
					setScheduleText('Now');
				} else {
					const [, monthStr, dayStr] = firstSlot.date.split('-');
					setScheduleText(`${monthStr}/${dayStr}`);
				}
			} catch (error) {
				console.warn('Error fetching times:', error);
			}
		};

		void fetchFirstAppointment();
	}, [officeInfo, appName, appVersion, type, url]);

	return { officeInfo, siteUrl, appointment, scheduleText };
}
